import { useEffect, useState, type RefObject } from "react";

/**
 * Telestration-replay visibility controller for note-mode playback
 * in the focused feedback player.
 *
 * Ports the legacy `_loadFeedbackVideoForNote` play/pause/seeked
 * listener trio (`js/coaching/feedback-player.js:601-623`). A
 * coaching note's `drawing` is a **freeze-frame** overlay pinned
 * to the note's `timestamp_seconds` — not a moving annotation.
 * The rule for whether it's visible:
 *
 *   - **paused AND playhead ≤ timestamp (+0.05s tol)** → SHOW.
 *     The player is parked at (or scrubbed back to) the coaching
 *     moment; the telestration is the point of the note.
 *   - **playing** → HIDE. The drawing would smear over moving
 *     video; the player pressed Play to watch, not study the
 *     freeze-frame.
 *   - **paused but playhead past the timestamp** → HIDE. They
 *     paused somewhere after the moment — the drawing doesn't
 *     belong to that frame.
 *
 * Wired to the `<video>`'s `play` / `pause` / `seeked` events
 * (plus a seed read on mount, since the element may already be
 * parked at the timestamp). Returns a boolean the modal uses to
 * choose between rendering the note's drawing and an empty one.
 */

const TIMESTAMP_TOLERANCE = 0.05;

/** Pure visibility rule — extracted so it's unit-testable without
 * a DOM `<video>`. Given the playhead state + the note's
 * timestamp, returns whether the telestration should be visible. */
export function shouldShowTelestration(
  state: { currentTime: number; paused: boolean },
  timestampSeconds: number,
): boolean {
  if (!state.paused) return false;
  return state.currentTime <= timestampSeconds + TIMESTAMP_TOLERANCE;
}

export type UseTelestrationReplayOptions = {
  /** The `<video>` element being played. */
  videoRef: RefObject<HTMLVideoElement | null>;
  /** The note's `timestamp_seconds` — the freeze-frame moment. */
  timestampSeconds: number;
  /** When false the hook doesn't bind — lets the modal mount it
   * unconditionally and gate by mode. Returns `false` when
   * disabled. */
  enabled: boolean;
};

/** Returns whether the note's telestration overlay should be
 * visible right now, reactively tracking the video's play state. */
export function useTelestrationReplay({
  videoRef,
  timestampSeconds,
  enabled,
}: UseTelestrationReplayOptions): boolean {
  const el = videoRef.current;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled || !el) {
      setVisible(false);
      return;
    }

    const sync = () => {
      setVisible(
        shouldShowTelestration(
          { currentTime: el.currentTime, paused: el.paused },
          timestampSeconds,
        ),
      );
    };
    // Seed immediately — the element may already be parked at the
    // timestamp (the note-mode loader seeks there on
    // `loadedmetadata`, which can fire before this effect binds).
    sync();
    el.addEventListener("play", sync);
    el.addEventListener("pause", sync);
    el.addEventListener("seeked", sync);
    // `loadedmetadata` covers the case where the seek-to-timestamp
    // lands after this effect already ran its seed `sync()`.
    el.addEventListener("loadedmetadata", sync);
    return () => {
      el.removeEventListener("play", sync);
      el.removeEventListener("pause", sync);
      el.removeEventListener("seeked", sync);
      el.removeEventListener("loadedmetadata", sync);
    };
  }, [el, timestampSeconds, enabled]);

  return visible;
}
